import { useEffect, useMemo, useState } from 'react'
import {
  useEditRequestBillingCompanyBranchesQuery,
  useEditRequestBillingCompanyMutation,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import ModalForm from '@/gf/components/ModalForm'
import Select from '@/gf/components/next/Select'
import Spinner from '@/gf/components/Spinner'
import useMsgs from '@/gf/hooks/useMsgs'

const EditRequestBillingCompanyModal = ({
  open,
  onClose,
  currentBillingCompanyId,
  requestId,
  refetch,
}: {
  open: boolean
  onClose: () => void
  currentBillingCompanyId: string | null | undefined
  requestId: string
  refetch: () => Promise<unknown>
}) => {
  const [_msgs, messages] = useMsgs()
  const client = useGqlClient()
  const [billingCompanyId, setBillingCompanyId] = useState(currentBillingCompanyId || '')
  const [editRequestBillingCompany] = useEditRequestBillingCompanyMutation({ client })

  useEffect(() => {
    if (open) setBillingCompanyId(currentBillingCompanyId || '')
  }, [open, currentBillingCompanyId])

  const { data, loading, error } = useEditRequestBillingCompanyBranchesQuery({ client })

  // Codes are not unique inside an org, so the name always shows and the code is only
  // a prefix - it is what people match against their ERP's company code.
  const options = useMemo(
    () =>
      (data?.allBranches ?? []).map(({ id, name, code }) => ({
        label: code ? `${code} ${name}` : name,
        value: id,
      })),
    [data]
  )

  const selectedOption = useMemo(
    () => options.find((o) => o.value === billingCompanyId) ?? null,
    [options, billingCompanyId]
  )

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!billingCompanyId) {
      messages.add('Pick a billing company', 'negative')
      return
    }

    editRequestBillingCompany({ variables: { id: requestId, billingCompanyId } })
      // Wait for the refresh, so success is never announced beside a stale value.
      // A refresh that fails is still not a lost edit, so it says so rather than
      // reporting the change itself as failed.
      .then(() =>
        refetch().then(
          () => messages.add('Billing company updated', 'positive'),
          () => messages.add('Billing company updated — reload to see it', 'positive')
        )
      )
      .catch((err) => messages.add(err?.message || 'Error updating billing company', 'negative'))
      .finally(onClose)
  }

  return (
    <ModalForm
      title="Edit Billing Company"
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      submitButtonText="Save"
    >
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner />
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm">
          Failed to load billing companies. Please try again.
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Billing company</label>
          <Select
            options={options}
            value={selectedOption}
            onChange={(o) => o && setBillingCompanyId(o.value)}
            placeholder="Select Billing Company"
          />
          <p className="text-sm text-gray-500">
            The invoice, purchase order and billing address on this request all follow the billing
            company. A purchase order already sent to a vendor keeps the company it was sent under.
          </p>
        </div>
      )}
    </ModalForm>
  )
}

export default EditRequestBillingCompanyModal
